// Utility functions for conditional loading of tracking scripts

declare global {
  interface Window {
    _mfq?: any[]
    dataLayer: any[]
    gtag?: (...args: any[]) => void
    gtm?: boolean
  }
}

let mouseflowLoaded = false
let appInsightsInitialized = false
let gtmLoaded = false

/**
 * Conditionally load Mouseflow based on consent
 */
export function loadMouseflow(projectId: string) {
  if (mouseflowLoaded) return
  
  try {
    // Initialize Mouseflow queue
    window._mfq = window._mfq || []
    
    // Create script element
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.src = `//cdn.mouseflow.com/projects/${projectId}.js`
    
    // Add to head
    const head = document.getElementsByTagName('head')[0]
    head.appendChild(script)
    
    mouseflowLoaded = true
    console.log('Mouseflow loaded with project ID:', projectId)
  } catch (error) {
    console.error('Failed to load Mouseflow:', error)
  }
}

/**
 * Load Application Insights. Call only after analytics consent.
 */
export function loadAppInsights(connectionString: string | undefined) {
  if (appInsightsInitialized) return
  
  try {
    // Dynamic import to avoid loading Application Insights bundle if not needed
    import('./appinsights-config').then(({ initializeAppInsights }) => {
      const instance = initializeAppInsights(connectionString)
      if (instance) {
        appInsightsInitialized = true
        console.log('Application Insights initialized')
      }
    }).catch((error) => {
      console.error('Failed to initialize Application Insights:', error)
    })
  } catch (error) {
    console.error('Failed to load Application Insights:', error)
  }
}

/**
 * Initialize Google Tag Manager with actual implementation
 */
export function loadGoogleTagManager(gtmId: string) {
  if (gtmLoaded) return
  
  try {
    // Initialize dataLayer
    window.dataLayer = window.dataLayer || []
    
    // GTM initialization function
    function gtag(...args: any[]) {
      window.dataLayer!.push(args)
    }
    window.gtag = gtag

    // Consent Mode default denied before gtag.js / gtm.js.
    gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'denied',
      personalization_storage: 'denied',
      security_storage: 'granted',
      wait_for_update: 500,
    })

    // Create and inject GTM script
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gtmId}`
    
    const head = document.getElementsByTagName('head')[0]
    head.appendChild(script)
    
    // Also inject the main GTM container script
    const gtmScript = document.createElement('script')
    gtmScript.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `
    head.appendChild(gtmScript)
    
    // Add noscript fallback to body
    const noscript = document.createElement('noscript')
    const iframe = document.createElement('iframe')
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`
    iframe.height = '0'
    iframe.width = '0'
    iframe.style.display = 'none'
    iframe.style.visibility = 'hidden'
    noscript.appendChild(iframe)
    document.body.appendChild(noscript)
    
    gtmLoaded = true
    console.log('Google Tag Manager loaded with ID:', gtmId)
    
  } catch (error) {
    console.error('Failed to load GTM:', error)
  }
}

/**
 * Update GTM consent status
 */
export function updateGTMConsent(consent: {
  analytics: boolean
  marketing: boolean
}) {
  if (!gtmLoaded || !window.gtag) return
  
  try {
    window.gtag('consent', 'update', {
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      ad_user_data: consent.marketing ? 'granted' : 'denied',
      ad_personalization: consent.marketing ? 'granted' : 'denied',
      functionality_storage: 'denied',
      personalization_storage: 'denied',
      security_storage: 'granted',
    })
    console.log('GTM consent updated:', consent)
  } catch (error) {
    console.error('Failed to update GTM consent:', error)
  }
}

/**
 * Load optional trackers only for the categories the visitor accepted.
 * Google Tag Manager is not inserted when both analytics and marketing are off.
 */
export function initializeTracking(config: {
  mouseflowProjectId?: string
  gtmId?: string
}, consent: {
  analytics: boolean
  marketing: boolean
}) {
  if (config.gtmId && (consent.analytics || consent.marketing)) {
    loadGoogleTagManager(config.gtmId)
    updateGTMConsent(consent)
  } else if (gtmLoaded) {
    updateGTMConsent(consent)
  }

  if (consent.analytics && config.mouseflowProjectId) {
    loadMouseflow(config.mouseflowProjectId)
  }
}

